import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FaX } from 'react-icons/fa6';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Keyboard, Pagination, Zoom } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/zoom';

const SWIPE_DOWN_THRESHOLD = 80;

const ImageLightbox = ({
    show,
    onHide,
    src,
    alt,
    images = [],
    initialIndex = 0,
    onImageChange,
}) => {
    const swiperRef = useRef(null);
    const touchStartYRef = useRef(0);
    const touchStartXRef = useRef(0);
    const [isZoomed, setIsZoomed] = useState(false);

    const lightboxImages = useMemo(() => {
        const orderedImages = [...new Set((images || []).filter(Boolean))];

        if (src && !orderedImages.includes(src)) {
            orderedImages.unshift(src);
        }

        return orderedImages.length > 0 ? orderedImages : ['/images/default.jpg'];
    }, [images, src]);

    const safeInitialIndex = Math.min(
        Math.max(initialIndex, 0),
        Math.max(lightboxImages.length - 1, 0)
    );

    useEffect(() => {
        if (!show || !swiperRef.current) return;

        swiperRef.current.slideTo(safeInitialIndex, 0);
        if (swiperRef.current.zoom?.scale > 1) {
            swiperRef.current.zoom.out();
        }
        setIsZoomed(false);
    }, [show, safeInitialIndex]);

    // Reset zoom state when lightbox closes
    useEffect(() => {
        if (!show) setIsZoomed(false);
    }, [show]);

    const handleTouchStart = useCallback((e) => {
        touchStartYRef.current = e.touches[0].clientY;
        touchStartXRef.current = e.touches[0].clientX;
    }, []);

    const handleTouchEnd = useCallback((e) => {
        if (isZoomed) return;
        const dy = e.changedTouches[0].clientY - touchStartYRef.current;
        const dx = Math.abs(e.changedTouches[0].clientX - touchStartXRef.current);
        // Only dismiss on a clear downward drag, not on horizontal swipes
        if (dy > SWIPE_DOWN_THRESHOLD && dy > dx * 1.5) {
            onHide();
        }
    }, [isZoomed, onHide]);

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            fullscreen
            contentClassName="image-lightbox-content"
            className="image-lightbox-modal"
        >
            {/* Desktop close button — top-right, hidden on mobile */}
            <Modal.Header
                className="image-lightbox-header border-0 p-3 position-absolute top-0 end-0 d-none d-md-flex"
                style={{ zIndex: 1100 }}
            >
                <Button
                    variant="dark"
                    onClick={(e) => {
                        e.stopPropagation();
                        onHide();
                    }}
                    className="rounded-circle p-2 d-flex align-items-center justify-content-center border-0"
                    style={{
                        width: '45px',
                        height: '45px',
                        opacity: 0.9,
                        backgroundColor: 'rgba(0,0,0,0.7)',
                    }}
                    aria-label="Cerrar visor de imágenes"
                >
                    <FaX size={20} />
                </Button>
            </Modal.Header>

            <Modal.Body
                className="image-lightbox-body p-0 d-flex align-items-center justify-content-center"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                <Swiper
                    key={lightboxImages.join('|')}
                    modules={[Zoom, Pagination, Keyboard, A11y]}
                    zoom={{
                        maxRatio: 4,
                        minRatio: 1,
                        toggle: false,
                    }}
                    pagination={
                        lightboxImages.length > 1
                            ? {
                                  clickable: true,
                              }
                            : false
                    }
                    keyboard={{ enabled: true }}
                    initialSlide={safeInitialIndex}
                    onSwiper={(swiper) => {
                        swiperRef.current = swiper;
                    }}
                    onSlideChange={(swiper) => {
                        const nextImage = lightboxImages[swiper.activeIndex];
                        if (nextImage) {
                            onImageChange?.(nextImage);
                        }
                    }}
                    onZoomChange={(_swiper, scale) => {
                        setIsZoomed(scale > 1);
                    }}
                    className="image-lightbox-swiper"
                >
                    {lightboxImages.map((image, index) => (
                        <SwiperSlide key={`${image}-${index}`} className="image-lightbox-slide">
                            <div className="swiper-zoom-container image-lightbox-zoom-container">
                                <img
                                    src={image}
                                    alt={`${alt} ${index + 1}`}
                                    className="image-lightbox-image"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = '/images/default.jpg';
                                    }}
                                />
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>

                {/* Mobile close bar — fixed bottom, always reachable by thumb */}
                <div className="image-lightbox-mobile-bar d-flex d-md-none">
                    {isZoomed && (
                        <span className="image-lightbox-zoom-hint">
                            Pellizca para alejar
                        </span>
                    )}
                    <button
                        type="button"
                        className="image-lightbox-close-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onHide();
                        }}
                        aria-label="Cerrar visor de imágenes"
                    >
                        <FaX size={14} />
                        <span>Cerrar</span>
                    </button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default ImageLightbox;
